import { useEffect, useMemo, useState } from "react"
import { ShopAlgolia } from "types/algolia/shopAlgolia"
import useAlgolia from "use-algolia"
import { localAlgoliaIndexPre, localAlgoliaVars } from "util-functions/init"

export function useFilteredShops(
  query: string,
  hitsPerPage = 10,
  userLocation: { lat: number; lng: number } | null,
  enabled = true
) {
  const [allShops, setAllShops] = useState<ShopAlgolia[]>([])
  const [page, setPage] = useState(0)
  const [hasMore, setHasMore] = useState(true)

  const filters = useMemo(() => {
    const locationFilter = userLocation
      ? {
          aroundLatLng: `${userLocation.lat},${userLocation.lng}`,
          aroundRadius: "all" as const,
        }
      : {}

    return {
      query,
      hitsPerPage,
      page,
      ...locationFilter,
      filters: "showInApp:true",
    }
  }, [userLocation?.lat, userLocation?.lng, query, hitsPerPage, page])

  // adding the enabled check to make sure we only make the request when enabled is `true`
  const [searchState, requestDispatch, getMore] = useAlgolia<ShopAlgolia>(
    enabled ? localAlgoliaVars.appId : "",
    enabled ? "e31e3523b1ae90dbf8c42361ed3a8a58" : "",
    enabled ? `${localAlgoliaIndexPre}_SHOPS` : "",
    enabled ? filters : { query: "", hitsPerPage: 0 }
  )

  useEffect(() => {
    if (enabled) {
      requestDispatch(filters)
    }
  }, [filters, enabled])

  useEffect(() => {
    if (enabled) {
      setAllShops([])
      setPage(0)
      setHasMore(true)
    }
  }, [query, userLocation, enabled])

  useEffect(() => {
    if (searchState.response?.hits) {
      setAllShops(prevShops => [...prevShops, ...searchState.response!.hits])
      setHasMore(searchState.response.page < searchState.response.nbPages - 1)
    }
  }, [searchState.response])

  return {
    shops: allShops,
    loading: searchState.loading,
    hasMore,
    loadMore: getMore,
  }
}
